import { FormikState } from "formik";
import { BusinessCustomerEnum, BusinessEnum, ConsumerEnum } from "./constants";
import {
  AddressFields,
  BusinessFields,
  ConsumerFields,
  CompanyData,
  CustomerDataBusiness,
  CustomerDataConsumer,
  FNPInitialStateData,
  FnpStep,
  FormattedFNPInitialStateData,
  FormattedMNPInitialStateData,
  MNPInitialStateData,
  MnpStep,
  ResponsiblePersonFields,
  XnpFlow,
} from "./types";
import { ResponsibleOrAuthorizedFields } from "./ResponsibleOrAuthorizedForm";

interface ShouldHaveFormikErrorFunction {
  <T>(formikObject: FormikState<T>, field: keyof T): boolean;
}

export const shouldHaveFormikError: ShouldHaveFormikErrorFunction = (
  formikObject,
  field
) => {
  return Boolean(formikObject.errors[field] && formikObject.touched[field]);
};

export const isBusinessEnumValue = (value: string) =>
  Object.values(BusinessEnum).includes(value as BusinessEnum);

export const isConsumerEnumValue = (value: string) =>
  Object.values(ConsumerEnum).includes(value as ConsumerEnum);

export const formatMNPInitialStateDataForSubmit = (
  data: MNPInitialStateData
): FormattedMNPInitialStateData => {
  const customer = {};
  const address = {};
  const company = {};
  const portingItem = data.portingItem;
  const portingDate = data.portingDate;
  const storeId = data.storeId;

  if (isBusinessEnumValue(data.customer.userType as string)) {
    const customerData = data.customer as BusinessFields & ResponsibleOrAuthorizedFields & { email: string };

    (customer as CustomerDataBusiness).subtype = ConsumerEnum.PERSON;
    (customer as CustomerDataBusiness).email = customerData.email;

    (customer as CustomerDataBusiness).type = BusinessCustomerEnum.RESPONSIBLE_PERSON;
    (customer as CustomerDataBusiness).firstName = customerData.firstName;
    (customer as CustomerDataBusiness).lastName = customerData.lastName;
    (customer as CustomerDataBusiness).jmbg = customerData.jmbg;
  }

  if (isConsumerEnumValue(data.customer.userType as string)) {
    const customerData = data.customer as ConsumerFields & { email: string };

    (customer as CustomerDataConsumer).type = "consumer";
    (customer as CustomerDataConsumer).subtype = customerData.userType;
    (customer as CustomerDataConsumer).firstName = customerData.firstName;
    (customer as CustomerDataConsumer).lastName = customerData.lastName;
    (customer as CustomerDataConsumer).jmbg = customerData.jmbg;
    (customer as CustomerDataConsumer).email = customerData.email;
    (customer as CustomerDataConsumer).idNumber = customerData.idNumber;
    (customer as CustomerDataConsumer).passportNumber = customerData.passportNumber;
    (customer as CustomerDataConsumer).issuedByCountry = customerData.issuedByCountry;
  }

  (address as AddressFields).zipCode = data.address.zipCode;
  (address as AddressFields).city = data.address.city;
  (address as AddressFields).municipality = data.address.municipality;
  (address as AddressFields).neighborhood = data.address.neighborhood;
  (address as AddressFields).street = data.address.street;
  (address as AddressFields).addressType = data.address.addressType;
  (address as AddressFields).buildingNumber = data.address.buildingNumber;

  if (isBusinessEnumValue(data.customer.userType as string)) {
    const companyData = data.customer as BusinessFields;

    (company as CompanyData).type = companyData.userType;
    (company as CompanyData).name = companyData.name;
    (company as CompanyData).mb = companyData.mb;
    (company as CompanyData).pib = companyData.pib;
    (company as CompanyData).registrationId = companyData.registrationId;
    (company as CompanyData).country = companyData.country;

    if ((data.customer as BusinessFields & ResponsibleOrAuthorizedFields).approvedOrAuthorized === BusinessCustomerEnum.AUTHORIZED_PERSON) {
      const customerWithAuthorizedPersonData = (data.customer as BusinessFields & ResponsibleOrAuthorizedFields);
      (company as CompanyData).authorizedPersonFirstName = customerWithAuthorizedPersonData.authorizedFirstName;
      (company as CompanyData).authorizedPersonLastName = customerWithAuthorizedPersonData.authorizedLastName;
      (company as CompanyData).authorizedPersonJmbg = customerWithAuthorizedPersonData.authorizedJmbg;
    }
  }

  return {
    type: "mnp",
    step: "initialState",
    data: {
      customer,
      company,
      address: address as Omit<AddressFields, "isStreetRequired">,
      storeId,
      portingDate,
      portingItem,
    },
  };
};

export const formatFNPInitialStateDataForSubmit = (
  data: FNPInitialStateData
): FormattedFNPInitialStateData => {
  const customer = {};
  const address = {};
  const installationAddress = {};
  const company = {};
  const portingDate = data.portingDate;
  const portingItem = data.portingItem;

  if (isBusinessEnumValue(data.customer.userType as string)) {
    const customerData = data.customer as BusinessFields & ResponsiblePersonFields & { contactPhone: string };

    (customer as CustomerDataBusiness).subtype = ConsumerEnum.PERSON;
    (customer as CustomerDataBusiness).contactPhone = customerData.contactPhone;

    (customer as CustomerDataBusiness).type = BusinessCustomerEnum.RESPONSIBLE_PERSON;
    (customer as CustomerDataBusiness).firstName = customerData.firstName;
    (customer as CustomerDataBusiness).lastName = customerData.lastName;
    (customer as CustomerDataBusiness).jmbg = customerData.jmbg;
  }

  if (isConsumerEnumValue(data.customer.userType as string)) {
    const customerData = data.customer as ConsumerFields & { contactPhone: string };

    (customer as CustomerDataConsumer).type = "consumer";
    (customer as CustomerDataConsumer).subtype = customerData.userType;
    (customer as CustomerDataConsumer).firstName = customerData.firstName;
    (customer as CustomerDataConsumer).lastName = customerData.lastName;
    (customer as CustomerDataConsumer).jmbg = customerData.jmbg;
    (customer as CustomerDataConsumer).contactPhone = customerData.contactPhone;
    (customer as CustomerDataConsumer).idNumber = customerData.idNumber;
    (customer as CustomerDataConsumer).passportNumber = customerData.passportNumber;
    (customer as CustomerDataConsumer).issuedByCountry = customerData.issuedByCountry;
  }

  (address as AddressFields).zipCode = data.address.zipCode;
  (address as AddressFields).city = data.address.city;
  (address as AddressFields).municipality = data.address.municipality;
  (address as AddressFields).neighborhood = data.address.neighborhood;
  (address as AddressFields).street = data.address.street;
  (address as AddressFields).addressType = data.address.addressType;
  (address as AddressFields).buildingNumber = data.address.buildingNumber;

  (installationAddress as AddressFields).zipCode = data.installmentAddress.zipCode;
  (installationAddress as AddressFields).city = data.installmentAddress.city;
  (installationAddress as AddressFields).municipality = data.installmentAddress.municipality;
  (installationAddress as AddressFields).neighborhood = data.installmentAddress.neighborhood;
  (installationAddress as AddressFields).street = data.installmentAddress.street;
  (installationAddress as AddressFields).addressType = data.installmentAddress.addressType;
  (installationAddress as AddressFields).buildingNumber = data.installmentAddress.buildingNumber;

  if (isBusinessEnumValue(data.customer.userType as string)) {
    const companyData = data.customer as BusinessFields;

    (company as CompanyData).type = companyData.userType;
    (company as CompanyData).name = companyData.name;
    (company as CompanyData).mb = companyData.mb;
    (company as CompanyData).pib = companyData.pib;
    (company as CompanyData).registrationId = companyData.registrationId;
    (company as CompanyData).country = companyData.country;

    if ((data.customer as BusinessFields & ResponsibleOrAuthorizedFields).approvedOrAuthorized === BusinessCustomerEnum.AUTHORIZED_PERSON) {
      const customerWithAuthorizedPersonData = (data.customer as BusinessFields & ResponsibleOrAuthorizedFields);
      (company as CompanyData).authorizedPersonFirstName = customerWithAuthorizedPersonData.authorizedFirstName;
      (company as CompanyData).authorizedPersonLastName = customerWithAuthorizedPersonData.authorizedLastName;
      (company as CompanyData).authorizedPersonJmbg = customerWithAuthorizedPersonData.authorizedJmbg;
    }
  }

  return {
    type: "fnp",
    step: "personalDataState",
    data: {
      customer,
      address: address as Omit<AddressFields, "isStreetRequired">,
      installationAddress: installationAddress as Omit<AddressFields, "isStreetRequired">,
      company,
      portingDate,
      portingItem,
    },
  };
};

export const trimAdditionalNumbers = (additionalNumbers: string) => {
  const additionalNumbersArray = additionalNumbers
    .split(",")
    .map((number) => number.trim());
  return additionalNumbersArray.join(",");
};

export const generateDataForStepTransition = (step: MnpStep | FnpStep, flow: XnpFlow = "mnp") => {
  return {
    step,
    type: flow,
    data: {},
  };
};

export const dateObjectToUniformFormat = (date: Date): string => {
  return date.toLocaleDateString('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

export const dateObjectToDisplayFormat = (date: Date): string => {
  return date.toLocaleDateString('sr-RS');
};
